import { Box, Button, Card, CardContent, Divider, Stack, Typography, Chip } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useLanguage } from '../../../utils/i18n';

export default function GeolocationCard({ item, isOpen, onToggle, onClickHeader, onClickVehicle }) {
  const { t } = useLanguage();
  const getLabelColor = (itemLoc) => {
    const type = itemLoc?.location_type;
    const t = (type || '').toLowerCase();
    if (t === 'pit') return { label: 'PIT', color: '#1565c0', bg: '#ffffff' };
    if (t === 'jetty' || t === 'Jetty') return { label: 'Jetty', color: '#2e7d32', bg: '#ffffff' };
    if (t === 'geofence') {
      const geoLabel = itemLoc?.join?.polygon_type_name || 'Geofence';
      const geoColor = itemLoc?.join?.polygon_type_outline_color || '#7b1fa2';
      return {
        label: geoLabel.charAt(0).toUpperCase() + geoLabel.slice(1),
        color: geoColor,
        bg: '#ffffff'
      };
    }
    return { label: type || 'Location', color: '#424242', bg: '#f5f5f5' };
  };

  const typeConfig = getLabelColor(item);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: 1,
        mb: 1,
        boxShadow: '0 6px 18px rgba(8,14,30,0.04)'
      }}
    >
      <CardContent sx={{ py: 1.25, px: 1.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box onClick={() => onClickHeader(item)} sx={{ cursor: 'pointer', userSelect: 'none', flex: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {item.name || item.pool_name || item.geofence_name || item.transit_point_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label={typeConfig.label}
                variant="outlined"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  borderColor: typeConfig.color,
                  bgcolor: typeConfig.bg,
                  color: typeConfig.color
                }}
              />
              <Chip
                label={item.total_vehicle || 0}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 'bold'
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center', color: 'text.secondary' }}>
              <LocalShippingOutlinedIcon fontSize="small" />
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {item.join?.company_name || item.pool_code || item.address || '-'}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Button onClick={() => onToggle(item.id)} sx={{ minWidth: 'auto', p: 1 }}>
              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Button>
          </Box>
        </Box>
        {isOpen && (
          <Stack sx={{ mt: 1, px: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('mapsTracking.province', 'Province')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item.join?.area_name || item.city || '-'}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('mapsTracking.contact', 'Contact')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item.pic_name || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('mapsTracking.city', 'City')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item.city || item.join?.country_name || '-'}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('mapsTracking.contactNumber', 'Contact Number')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item.pic_phone || '-'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('mapsTracking.vehicleGroup', 'Vehicle Group')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item.pool_group || item.join?.pool_group_name || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('mapsTracking.totalVehicle', 'Total Vehicle')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item.total_vehicle || 0}
                </Typography>
              </Box>
            </Box>

            {/* VEHICLE LIST */}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                {t('mapsTracking.vehicleList', 'Vehicle List')}
              </Typography>

              {(!item.vehicles_raw || item.vehicles_raw.length === 0) && (
                <Box sx={{ py: 0.7, color: 'text.secondary', fontSize: '0.85rem', fontStyle: 'italic' }}>
                  {t('mapsTracking.noVehicleInLocation', 'Tidak ada kendaraan di lokasi ini.')}
                </Box>
              )}

              {item.vehicles_raw?.map((v, idx) => {
                const label = v.vehicle_license_plate || v.vehicle_name || v.info?.name || 'Unknown Vehicle';
                const isDelay = String(v.status || '')
                  .toLowerCase()
                  .includes('delay');
                return (
                  <Box
                    key={v.vehicle ?? v.info?.id ?? idx}
                    onClick={() => onClickVehicle(v)}
                    sx={{
                      py: 0.7,
                      borderBottom: '1px solid #f0f0f0',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      color: '#111827',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover': { color: '#0072DA', pl: 0.5, transition: 'all 0.2s ease' }
                    }}
                  >
                    <span>{label}</span>
                    {isDelay && <Chip label="Delay" color="warning" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                  </Box>
                );
              })}
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
